import {
  Popover,
  PopoverProps,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ellipsize from 'ellipsize';
import React, {
  FC,
  Fragment,
  ReactNode,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Chip } from '@sbu/components.chip';
import { Button, ButtonProps, Tooltip } from '@sumologic/yama-ds-components';
import { AngleDownIcon, CalendarIcon } from '@sumologic/yama-ds-icons';

import filterCss from 'shared/components/internal/Filter/FilterChips/FilterChips.module.scss';

import css from './RecordSearchDateRangeSelect.module.scss';
import { RecordsSearchCustomRangeSelect } from './RecordsSearchCustomRangeSelect';
import {
  labelForValue,
  RecordsSearchRelativeTimeSelect,
  RelativeValueOptions,
} from './RecordsSearchRelativeTimeSelect';
import { RecordSearchDateRangeValue } from './types';
import { dateRangeFromValue } from './utils';

const isRelativeTimeRange = (selectedValue: RecordSearchDateRangeValue) =>
  typeof selectedValue === 'string';

interface RecordSearchDateRangeSelectProps {
  buttonProps?: ButtonProps;
  PopoverProps?: Omit<PopoverProps, 'open'>;
  relativeOptions?: RelativeValueOptions;
  selectedValue: RecordSearchDateRangeValue;
  variant?: 'chip' | 'button' | 'buttonText';
  onSelectValue: (value: RecordSearchDateRangeValue) => void;
}

export const RecordSearchDateRangeSelect: FC<
  React.PropsWithChildren<RecordSearchDateRangeSelectProps>
> = ({
  buttonProps = {},
  PopoverProps,
  relativeOptions,
  selectedValue,
  variant = 'chip',
  onSelectValue,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [activeTab, setActiveTab] = useState<string>(
    isRelativeTimeRange(selectedValue) ? 'relative' : 'custom'
  );
  const open = Boolean(anchorEl);

  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const valueLabel = labelForValue(selectedValue, relativeOptions);
  const selectButtonTooltip = useMemo(() => {
    let tooltip: ReactNode = '';

    if (
      (variant === 'button' || variant === 'buttonText') &&
      isRelativeTimeRange(selectedValue)
    ) {
      const dateRange = dateRangeFromValue(selectedValue);
      tooltip = labelForValue(dateRange);
    }

    return tooltip;
  }, [selectedValue, variant]);

  return (
    <Fragment>
      {variant === 'chip' && (
        <div ref={ref}>
          <div className={filterCss.label}>
            <Typography color='text.secondary' className={filterCss.fieldLabel}>
              Timestamp (Req&apos;d)
            </Typography>
          </div>
          <Chip
            label={ellipsize(valueLabel, 15)}
            LabelProps={{
              'data-sumo-test': 'record-search-filter-timestamp-value',
            }}
            onLabelClick={handleMenuClick}
            title={`Click to edit ${valueLabel}`}
            sx={{
              width: '100%',
            }}
            variant='outlined'
          />
        </div>
      )}
      {variant === 'button' && (
        <div className={css.timeSelectButton} ref={ref}>
          <Tooltip title={selectButtonTooltip}>
            <Button
              className={css.timeSelectButton}
              onClick={handleMenuClick}
              endIcon={<AngleDownIcon />}
              color='secondary'
              {...buttonProps}
            >
              <span className={css.timeSelectButtonText}>{valueLabel}</span>
            </Button>
          </Tooltip>
        </div>
      )}
      {variant === 'buttonText' && (
        <div className={css.timeSelectButton} ref={ref}>
          <Tooltip title={selectButtonTooltip}>
            <Button
              aria-label={valueLabel}
              className={css.timeSelectButton}
              onClick={handleMenuClick}
              variant='text'
              startIcon={<CalendarIcon />}
              color='secondary'
              {...buttonProps}
            >
              {<span className={css.timeSelectButtonText}>{valueLabel}</span>}
            </Button>
          </Tooltip>
        </div>
      )}
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        onClose={handleMenuClose}
        open={open}
        {...PopoverProps}
      >
        <ToggleButtonGroup
          value={activeTab}
          onChange={(_e, value) => value !== null && setActiveTab(value)}
          exclusive
          sx={{
            p: 2,
          }}
        >
          <ToggleButton value='relative'>Relative</ToggleButton>
          <ToggleButton value='custom'>Custom</ToggleButton>
        </ToggleButtonGroup>
        {activeTab === 'relative' && (
          <RecordsSearchRelativeTimeSelect
            onDeactivate={handleMenuClose}
            onSelectValue={onSelectValue}
            relativeOptions={relativeOptions}
            selectedValue={selectedValue}
          />
        )}
        {activeTab === 'custom' && (
          <RecordsSearchCustomRangeSelect
            onDeactivate={handleMenuClose}
            onSelectValue={onSelectValue}
            selectedValue={selectedValue}
          />
        )}
      </Popover>
    </Fragment>
  );
};
